import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, PenLine, Loader2, FileText } from "lucide-react";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#111";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setEmpty(false); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setEmpty(true); onChange(""); };

  return (
    <div>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className="w-full h-40 border-2 border-dashed border-slate-300 rounded-lg bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-slate-400">{empty ? "Draw your signature above with your finger or mouse" : "Signed"}</span>
        <button className="text-xs text-slate-500 underline" onClick={clear} data-testid="signature-clear">Clear</button>
      </div>
    </div>
  );
}

export default function ContractSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await publicApi.get(`/public/contract/${token}`); setData(data); }
    catch (e) { setErr(e.response?.data?.detail || "Contract not found."); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setErr("");
    if (!name.trim() || !sig || !agree) { setErr("Please type your name, sign, and check the agreement box."); return; }
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/public/contract/${token}/sign`, { name, signature_image: sig, agree });
      setDone(data);
    } catch (e) { setErr(e.response?.data?.detail || "Something went wrong."); }
    finally { setSubmitting(false); }
  };

  if (err && !data) return <Centered><p className="text-slate-600" data-testid="contract-error">{err}</p></Centered>;
  if (!data) return <Centered><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></Centered>;

  const pc = data.primary_color || "#0f172a";

  if (done || data.signed) {
    const pdf = (done && done.pdf_url) || data.pdf_url;
    return (
      <Centered>
        <div className="max-w-md text-center" data-testid="contract-signed">
          <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: pc }} />
          <h1 className="text-2xl font-bold mb-2">Contract signed</h1>
          <p className="text-slate-600 text-sm leading-relaxed">Thank you. Your signed contract has been submitted. You're booked once <b>{data.business_name}</b> gives final acceptance — they'll email you to confirm.</p>
          {pdf && <a href={pdf.startsWith("http") ? pdf : (process.env.REACT_APP_BACKEND_URL + pdf)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold underline" style={{ color: pc }} data-testid="contract-pdf-link"><FileText className="h-4 w-4" /> Download signed PDF</a>}
        </div>
      </Centered>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" style={{ "--pc": pc }}>
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden" data-testid="contract-page">
        <div className="px-7 py-5 text-white" style={{ background: pc }}>
          <div className="text-lg font-bold">{data.business_name}</div>
          <div className="text-sm opacity-90">Event Booking Agreement</div>
        </div>
        <div className="px-7 py-6">
          <div className="text-sm text-slate-700 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_p]:my-2 [&_strong]:text-slate-900 [&_b]:text-slate-900 [&_a]:underline" dangerouslySetInnerHTML={{ __html: data.body_html }} />
          <div className="border-t border-slate-200 mt-6 pt-5">
            <h3 className="font-bold flex items-center gap-2 mb-3"><PenLine className="h-4 w-4" /> Sign to accept</h3>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Full legal name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your name" className="mb-4" data-testid="contract-name-input" />
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Signature</label>
            <SignaturePad onChange={setSig} />
            <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="contract-agree" />
              <span>I agree this is my electronic signature and I accept the terms above. I understand my booking is confirmed once {data.business_name} accepts.</span>
            </label>
            {err && <p className="text-red-600 text-sm mt-3" data-testid="contract-sign-error">{err}</p>}
            <Button className="w-full mt-5 text-white" style={{ background: pc }} onClick={submit} disabled={submitting} data-testid="contract-submit">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign &amp; submit
            </Button>
            <p className="text-xs text-slate-400 text-center mt-3">Secured by MorrCart · Signed electronically under ESIGN/UETA</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">{children}</div>;
}
